// Imports
import { useRef, useEffect, useMemo } from 'react';
import useCachedState from '../../../../foundation/front-end/utils/use-cached-state';
import useUpdateOnlyEffect from '../../../../foundation/front-end/utils/use-update-only-effect';
import { GetRegionsParameters, GetRegionsResponse } from '../../../../types/api/main/regions/get';
import storybookRegions from '../../storybook-regions';
import { Address } from '../..';
import { useAppDispatch } from '../../../../foundation/front-end/redux/hooks';
import apiActions from '../../../../integrations/api/redux/actions';


// Define options
interface Options {
	place?: Partial<Address>;
	shouldDisplay: (options: {
		name: keyof Address;
		labelAvailable?: boolean | undefined;
		fieldType?: 'INTERNAL' | 'EXTERNAL' | undefined;
	}) => boolean;
	initialPlace?: Partial<Address>;
	fallbackCountry?: string | null;
	displayPaymentProcessorSupportedRegionsOnly?: boolean;
}


// Define output
interface Output<T extends Partial<Address>> {
	regions: GetRegionsResponse | null;
	countrySetter: React.MutableRefObject<((value: string) => void) | undefined>;
	lastCountryCode: React.MutableRefObject<T['countryCode']>;
	countryDetails?: GetRegionsResponse[0];
}


/** React Hook that handles loading regions, and edge cases around the country field */
export default function useCountryFunctionality<T extends Partial<Address>>({
	place,
	shouldDisplay,
	initialPlace,
	fallbackCountry,
	displayPaymentProcessorSupportedRegionsOnly,
}: Options): Output<T> {
	// Use state
	const [regions, setRegions] = useCachedState<GetRegionsResponse | null>(
		(window as typeof window & { STORYBOOK?: true }).STORYBOOK === true ? storybookRegions : null,
		`${displayPaymentProcessorSupportedRegionsOnly ? 'payment-processor-' : ''}regions`
	);
	
	
	// Use ref
	const countrySetter = useRef<(value: string | number) => void>();
	const lastCountryCode = useRef<T['countryCode']>(initialPlace?.countryCode);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Load regions on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<GetRegionsParameters, undefined, GetRegionsResponse>({
				action: 'GET',
				uri: '/regions',
				completion: (json) => {
					setRegions(json.response);
				},
				parameters: {
					onlinePaymentsRegions: displayPaymentProcessorSupportedRegionsOnly,
				},
			})
		);
	}, [dispatch, setRegions, displayPaymentProcessorSupportedRegionsOnly]);
	
	
	// Set country select to fallback country when address changes from internal to external
	const previousAddressType = useRef(place?.type);
	
	useUpdateOnlyEffect(() => {
		// If regions are loading, then do nothing
		if (!regions) {
			return;
		}
		
		
		// Set country select when type is set to external and the country select is displayed
		if (previousAddressType.current !== place?.type) {
			previousAddressType.current = place?.type;
			
			if (
				place?.type === 'EXTERNAL' &&
				!place?.countryCode &&
				shouldDisplay({ name: 'countryCode', fieldType: 'EXTERNAL' }) &&
				fallbackCountry
			) {
				const country = regions.find((country) => country.twoLetterCode === fallbackCountry);
				
				if (country) {
					countrySetter.current?.(country.code);
				}
			}
		}
	}, [place?.type, place?.countryCode, regions, shouldDisplay, fallbackCountry]);
	
	
	// Correct last country code to be the same as the fallback country after regions load
	useEffect(() => {
		if (
			regions &&
			!shouldDisplay({ name: 'countryCode', fieldType: 'EXTERNAL' }) &&
			fallbackCountry &&
			!lastCountryCode.current
		) {
			lastCountryCode.current = regions.find((country) => country.twoLetterCode === fallbackCountry)?.code;
		}
	}, [regions, fallbackCountry, shouldDisplay]);
	
	
	// Build country details
	const countryDetails = useMemo(
		() =>
			(place?.countryCode || fallbackCountry) && regions
				? regions.find((country) =>
						place?.countryCode ? country.code === place?.countryCode : country.twoLetterCode === fallbackCountry
					)
				: undefined,
		[regions, place?.countryCode, fallbackCountry]
	);
	
	
	// Return
	return {
		countrySetter,
		lastCountryCode,
		regions,
		countryDetails,
	};
}
